/**
 * PayNowModal.jsx — Reusable Pay Now trigger for Hostel Booking & Admissions
 *
 * Usage:
 *   <PayNowModal
 *     context="HOSTEL_BOOKING"
 *     contextId={bookingId}
 *     amount="70959.26"
 *     currency="INR"
 *     label="Pay Hostel Fee"
 *     disabled={!booking.invoice_id}
 *   />
 *
 * Creates the payment intent on click, then navigates to /campus/pay with intent_id.
 * Simulator mode is surfaced clearly. (INV-PAY-SEC-05)
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, RefreshCw, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem("academiaos_token");
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

/**
 * context: "HOSTEL_BOOKING" | "ADMISSIONS"
 * contextId: booking_id or application_id
 * amount: string (display only, actual amount resolved server-side)
 * currency: string
 * label: button label
 * disabled: bool
 * applicantEmail: string (required for ADMISSIONS context)
 */
export default function PayNowModal({
  context,
  contextId,
  amount,
  currency = "INR",
  label = "Pay Now",
  disabled = false,
  applicantEmail,
  className = "",
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handlePayNow = async () => {
    if (!contextId) {
      toast.error("No context ID — cannot initiate payment");
      return;
    }
    setLoading(true);
    try {
      let response;
      if (context === "HOSTEL_BOOKING") {
        response = await axios.post(
          `${API}/api/pay/hostel/${contextId}/intent`,
          { payment_method_preference: "ONLINE" },
          { headers: authHeaders() }
        );
      } else if (context === "ADMISSIONS") {
        response = await axios.post(
          `${API}/api/pay/admissions/${contextId}/intent`,
          {
            applicant_email: applicantEmail || "applicant@academiaos.ai",
            payment_method_preference: "ONLINE",
          },
          { headers: authHeaders() }
        );
      } else {
        toast.error(`Unknown payment context: ${context}`);
        return;
      }

      const { intent_id, gateway_mode } = response.data;

      if (gateway_mode === "SIMULATOR") {
        toast.info("[SIMULATOR] Creating payment intent — no real charge");
      }

      navigate(
        `/campus/pay?intent_id=${intent_id}&amount=${encodeURIComponent(amount || response.data.amount)}&currency=${currency}&context_label=${encodeURIComponent(label)}`
      );
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "ALREADY_PAID") {
        toast.success("This has already been paid.");
      } else if (detail?.code === "NO_INVOICE") {
        toast.error("Generate an invoice first before initiating payment.");
      } else if (detail?.code === "BOOKING_NOT_PAYABLE") {
        toast.error(
          `Booking must be CONFIRMED or CHECKED_IN to pay. Current: ${detail.booking_status}`
        );
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message || "Failed to initiate payment"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handlePayNow}
      disabled={disabled || loading}
      data-testid={`btn-pay-now-${context?.toLowerCase()}`}
      className={`inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    >
      {loading ? (
        <RefreshCw size={15} className="animate-spin" />
      ) : (
        <CreditCard size={15} />
      )}
      {loading ? "Initiating…" : label}
    </button>
  );
}

/**
 * SimulatorNotice — small inline notice for simulator mode
 * Use alongside PayNowModal in parent components.
 */
export function SimulatorNotice() {
  return (
    <div className="flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-700">
      <AlertTriangle size={12} className="shrink-0 text-amber-500" />
      Simulator mode — no real payment will be processed
    </div>
  );
}
